import { useMemo } from "react";
import { SortDirection, SortOptions, SortOrderBy } from "@/constants/enums";
import type { ProductsQuery } from "@/viewModels/shop/features/useFilter/types";

//#region Type
export interface SortQuery {
  orderby: SortOrderBy;
  order: SortDirection;
}

export interface SortViewModel {
  selectedSortOption: SortOptions;
  updateSelectedSortOption: (selectedSortOption: SortOptions) => void;
}
//#endregion

// Helper
const mapSortOptionToQuery = (option: SortOptions): SortQuery => {
  const sortOptionToQueryMap: Record<SortOptions, SortQuery> = {
    [SortOptions.PRICE_DESC]: { orderby: SortOrderBy.PRICE, order: SortDirection.DESC },
    [SortOptions.PRICE_ASC]: { orderby: SortOrderBy.PRICE, order: SortDirection.ASC },
    [SortOptions.POPULARITY]: { orderby: SortOrderBy.POPULARITY, order: SortDirection.DESC },
    [SortOptions.DATE]: { orderby: SortOrderBy.DATE, order: SortDirection.DESC },
    [SortOptions.DEFAULT]: { orderby: SortOrderBy.DATE, order: SortDirection.DESC },
  };
  return sortOptionToQueryMap[option];
};

const mapFilterToSortOption = (
  orderby: SortOrderBy | undefined,
  order: SortDirection | undefined,
): SortOptions => {
  // 反向映射表：使用組合鍵 (orderby-order) 作為 key，查找比多個 if 更高效
  const filterToSortOptionMap: Record<string, SortOptions> = {
    [`${SortOrderBy.PRICE}-${SortDirection.DESC}`]: SortOptions.PRICE_DESC,
    [`${SortOrderBy.PRICE}-${SortDirection.ASC}`]: SortOptions.PRICE_ASC,
    [`${SortOrderBy.POPULARITY}-${SortDirection.DESC}`]: SortOptions.POPULARITY,
    [`${SortOrderBy.DATE}-${SortDirection.DESC}`]: SortOptions.DATE,
  };

  const key = `${orderby}-${order}`;
  return filterToSortOptionMap[key] ?? SortOptions.DEFAULT;
};

// Hook
const useSort = ({
  filter,
  updateFilter,
}: {
  filter: ProductsQuery;
  updateFilter: (updates: ProductsQuery) => void;
}): SortViewModel => {
  const selectedSortOption: SortOptions = useMemo(
    () => mapFilterToSortOption(filter.orderby, filter.order),
    [filter.orderby, filter.order],
  );

  const updateSelectedSortOption = (option: SortOptions) => {
    const sortQueryResult = mapSortOptionToQuery(option);
    updateFilter({
      orderby: sortQueryResult.orderby,
      order: sortQueryResult.order,
    });
  };

  return {
    selectedSortOption,
    updateSelectedSortOption,
  };
};

export default useSort;
